import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import slidingPuzzleQuestIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import { APP_SITE_ID } from "../../config/appSiteLinks";

const PUBLIC = process.env.PUBLIC_URL || "";
const INTRO_DURATION = "8s";

const flyTopLeft = keyframes`
    0%, 4% { opacity: 0; transform: translate3d(-55vw, -48vh, 0) rotate(-32deg) scale(.72); filter: blur(10px); }
    7% { opacity: 1; }
    22% { transform: translate3d(2.5%, 2.5%, 0) rotate(2deg) scale(1.035); filter: blur(0); }
    26%, 95% { opacity: 1; transform: translate3d(0, 0, 0) rotate(0) scale(1); filter: blur(0); }
    98%, 100% { opacity: 0; transform: translate3d(0, 12px, 0) scale(.98); }
`;

const flyTopRight = keyframes`
    0%, 7% { opacity: 0; transform: translate3d(52vw, -54vh, 0) rotate(38deg) scale(.7); filter: blur(11px); }
    10% { opacity: 1; }
    25% { transform: translate3d(-2.5%, 2.5%, 0) rotate(-2deg) scale(1.035); filter: blur(0); }
    29%, 95% { opacity: 1; transform: translate3d(0, 0, 0) rotate(0) scale(1); filter: blur(0); }
    98%, 100% { opacity: 0; transform: translate3d(0, 12px, 0) scale(.98); }
`;

const flyBottomLeft = keyframes`
    0%, 10% { opacity: 0; transform: translate3d(-58vw, 52vh, 0) rotate(29deg) scale(.74); filter: blur(10px); }
    13% { opacity: 1; }
    28% { transform: translate3d(2.5%, -2.5%, 0) rotate(-2deg) scale(1.035); filter: blur(0); }
    32%, 95% { opacity: 1; transform: translate3d(0, 0, 0) rotate(0) scale(1); filter: blur(0); }
    98%, 100% { opacity: 0; transform: translate3d(0, 12px, 0) scale(.98); }
`;

const flyBottomRight = keyframes`
    0%, 13% { opacity: 0; transform: translate3d(56vw, 48vh, 0) rotate(-36deg) scale(.72); filter: blur(11px); }
    16% { opacity: 1; }
    31% { transform: translate3d(-2.5%, -2.5%, 0) rotate(2deg) scale(1.04); filter: blur(0); }
    35%, 95% { opacity: 1; transform: translate3d(0, 0, 0) rotate(0) scale(1); filter: blur(0); }
    98%, 100% { opacity: 0; transform: translate3d(0, 12px, 0) scale(.98); }
`;

const characterPop = keyframes`
    0%, 30% { opacity: 0; transform: scale(.25) rotate(-8deg); filter: brightness(.7) drop-shadow(0 0 0 rgba(34, 211, 238, 0)); }
    34% { opacity: 1; transform: scale(1.16) rotate(2deg); filter: brightness(1.12) drop-shadow(0 0 34px rgba(34, 211, 238, .98)); }
    38% { transform: scale(.94) rotate(-1deg); }
    42% { transform: scale(1.04) rotate(0); filter: brightness(1.08) drop-shadow(0 0 42px rgba(34, 211, 238, .92)); }
    48%, 95% { opacity: 1; transform: scale(1) rotate(0); filter: brightness(1) drop-shadow(0 0 14px rgba(34, 211, 238, .4)); }
    98%, 100% { opacity: 0; transform: scale(.96); }
`;

const centerCover = keyframes`
    0%, 29% { opacity: 1; transform: rotate(-7deg) scale(.96); }
    34%, 100% { opacity: 0; transform: rotate(-7deg) scale(.7); }
`;

const finalIconReveal = keyframes`
    0%, 42% { opacity: 0; }
    48%, 95% { opacity: 1; }
    98%, 100% { opacity: 0; }
`;

const cyanPulse = keyframes`
    0%, 29% { opacity: 0; transform: scale(.72); }
    34% { opacity: 1; transform: scale(1.08); box-shadow: 0 0 28px 8px rgba(34, 211, 238, .9), 0 0 72px 20px rgba(14, 165, 233, .48); }
    40% { opacity: .45; transform: scale(.98); box-shadow: 0 0 16px 4px rgba(34, 211, 238, .55), 0 0 42px 10px rgba(14, 165, 233, .28); }
    46% { opacity: .9; transform: scale(1.025); box-shadow: 0 0 25px 7px rgba(34, 211, 238, .75), 0 0 58px 14px rgba(14, 165, 233, .38); }
    54%, 95% { opacity: .3; transform: scale(1); box-shadow: 0 0 14px 3px rgba(34, 211, 238, .38); }
    98%, 100% { opacity: 0; }
`;

const textReveal = keyframes`
    0%, 43% { opacity: 0; transform: translate3d(0, 70px, 0); filter: blur(8px); }
    54% { opacity: 1; transform: translate3d(0, -5px, 0); filter: blur(0); }
    58%, 95% { opacity: 1; transform: translate3d(0, 0, 0); filter: blur(0); }
    98%, 100% { opacity: 0; transform: translate3d(0, 18px, 0); }
`;

const ambientGlow = keyframes`
    0%, 100% { opacity: .45; transform: scale(.9); }
    50% { opacity: .9; transform: scale(1.08); }
`;

const INTRO_APPS = {
    [APP_SITE_ID.SLIDING_PUZZLE_QUEST]: {
        title: "Sliding Puzzle Quest",
        icon: slidingPuzzleQuestIcon,
        background: "linear-gradient(145deg, #07152f 0%, #123b68 48%, #08162d 100%)",
        glow: "rgba(56, 189, 248, 0.42)",
    },
    [APP_SITE_ID.JIGSAW_MUSE]: {
        title: "Jigsaw Muse",
        icon: `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`,
        background: "linear-gradient(145deg, #120925 0%, #38205c 50%, #160c2a 100%)",
        glow: "rgba(250, 204, 21, 0.38)",
    },
    [APP_SITE_ID.WORD_WHEEL_QUEST]: {
        title: "Word Wheel Quest",
        icon: `${PUBLIC}/ad/word-wheel-quest-app-icon-512.png`,
        background: "linear-gradient(145deg, #6b2205 0%, #f26a12 48%, #7a1f06 100%)",
        glow: "rgba(255, 237, 148, 0.45)",
    },
};

export default function TabletIntroPage() {
    const { appId } = useParams();
    const app = INTRO_APPS[appId] || INTRO_APPS[APP_SITE_ID.SLIDING_PUZZLE_QUEST];
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const restart = (event) => {
            if (event.key === "r" || event.key === "R" || event.key === " " || event.key === "Enter") {
                event.preventDefault();
                setAnimationKey((current) => current + 1);
            }
        };
        window.addEventListener("keydown", restart);
        return () => window.removeEventListener("keydown", restart);
    }, []);

    useEffect(() => {
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
        };
    }, []);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                background: app.background,
                color: "#fff",
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    width: "68vw",
                    height: "68vw",
                    maxWidth: 850,
                    maxHeight: 850,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${app.glow} 0%, rgba(255,255,255,0) 68%)`,
                    filter: "blur(12px)",
                    animation: `${ambientGlow} 6s ease-in-out infinite`,
                }}
            />
            <Box
                key={animationKey}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "clamp(20px, 3vh, 30px)",
                    transform: "translateY(-12px)",
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "clamp(260px, 28vw, 340px)",
                        height: "clamp(260px, 28vw, 340px)",
                        isolation: "isolate",
                    }}
                >
                    {[
                        { clipPath: "inset(0 50% 50% 0)", animation: flyTopLeft },
                        { clipPath: "inset(0 0 50% 50%)", animation: flyTopRight },
                        { clipPath: "inset(50% 50% 0 0)", animation: flyBottomLeft },
                        { clipPath: "inset(50% 0 0 50%)", animation: flyBottomRight },
                    ].map((piece, index) => (
                        <Box
                            key={piece.clipPath}
                            component="img"
                            src={app.icon}
                            alt={index === 0 ? `${app.title} app icon assembling` : ""}
                            aria-hidden={index !== 0}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 2,
                                width: "100%",
                                height: "100%",
                                display: "block",
                                objectFit: "cover",
                                borderRadius: "22%",
                                clipPath: piece.clipPath,
                                animation: `${piece.animation} ${INTRO_DURATION} cubic-bezier(.16, .84, .28, 1) infinite`,
                                willChange: "transform, opacity, filter",
                                transform: "translateZ(0)",
                            }}
                        />
                    ))}

                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            zIndex: 3,
                            left: "18%",
                            top: "18%",
                            width: "68%",
                            height: "64%",
                            borderRadius: "18%",
                            bgcolor: "#102d50",
                            transformOrigin: "center",
                            animation: `${centerCover} ${INTRO_DURATION} ease-in-out infinite`,
                        }}
                    />

                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            zIndex: 4,
                            left: "17%",
                            top: "16%",
                            width: "70%",
                            height: "68%",
                            borderRadius: "20%",
                            border: "4px solid rgba(103, 232, 249, .92)",
                            transform: "rotate(-7deg)",
                            animation: `${cyanPulse} ${INTRO_DURATION} ease-in-out infinite`,
                            willChange: "transform, opacity, box-shadow",
                        }}
                    />

                    <Box
                        component="img"
                        src={app.icon}
                        alt=""
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 5,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "22%",
                            clipPath: "polygon(15% 25%, 77% 14%, 89% 73%, 24% 86%)",
                            animation: `${characterPop} ${INTRO_DURATION} cubic-bezier(.2, 1.35, .35, 1) infinite`,
                            willChange: "transform, opacity, filter",
                        }}
                    />

                    <Box
                        component="img"
                        src={app.icon}
                        alt=""
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 6,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "22%",
                            boxShadow: `0 28px 70px rgba(0, 0, 0, 0.42), 0 0 65px ${app.glow}`,
                            animation: `${finalIconReveal} ${INTRO_DURATION} linear infinite`,
                            willChange: "opacity",
                        }}
                    />
                </Box>
                <Typography
                    component="div"
                    sx={{
                        fontSize: "clamp(44px, 5vw, 64px)",
                        lineHeight: 1,
                        fontWeight: 800,
                        letterSpacing: "0.02em",
                        textShadow: "0 6px 22px rgba(0, 0, 0, 0.5)",
                        animation: `${textReveal} ${INTRO_DURATION} cubic-bezier(.16, 1, .3, 1) infinite`,
                        willChange: "transform, opacity, filter",
                    }}
                >
                    {app.title}
                </Typography>
            </Box>
        </Box>
    );
}
